import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { SelectOption } from '@chiroup/components/form/Select';
import {
  itemToDataDescription,
  itemToOptionText,
} from '@chiroup/core/constants/DatabaseFeatureCommon';
import { useId, useMemo, useState } from 'react';
import { InfiniteData, QueryFunctionContext, useInfiniteQuery } from 'react-query';

// Ported from healthcom-app. The `/payors` endpoint lives on the
// schedule backend and derives `clinicId` from the active session
// (request | patient | kiosk), so we route through `api` (authed
// schedule API) — Amplify forwards whichever session token is
// available. The `clinicId` arg is still threaded through here for
// React Query cache scoping; it isn't sent on the wire.

const query = ({ limit = 10 }: { limit: number }) => {
  return async (context: QueryFunctionContext) => {
    const search = context.queryKey[2];

    return ChiroUpAPI.get('api', `/payors`, {
      queryParams: {
        limit,
        search,
        page: context.pageParam,
      },
    });
  };
};

type InstanceReferenceType = {
  [key: string]: any;
  ID: number;
};

const useDatabaseAutocomplete = (clinicId: number, limit = 10) => {
  const [search, setSearch] = useState('');

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery<{
    data: InstanceReferenceType[];
    count: number;
    page: number;
  }>([useId(), clinicId, search], query({ limit }), {
    getNextPageParam: lastGroup => {
      if (!lastGroup) return;

      return lastGroup.data?.length === 10 ? (lastGroup.page || 0) + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: {
      count: 0,
      page: 0,
      data: [] as InstanceReferenceType[],
      pageParams: [],
      pages: [],
    } as InfiniteData<any>,
  });

  const options: SelectOption[] = useMemo(() => {
    if (!data) return [];

    return data.pages
      .reduce((acc: any, curr) => {
        return [...acc, ...curr.data];
      }, [] as any[])
      .map((row: any) => ({
        text: itemToOptionText('payors', row),
        value: row.ID,
        data: { description: itemToDataDescription('payors', row) },
      }));
  }, [data]);

  const onChangeSearch = (value: string) => setSearch(value);

  return { options, isFetching, onChangeSearch, fetchNextPage, hasNextPage, search };
};

export default useDatabaseAutocomplete;
